import React from 'react';
import { Box, Typography, Select, MenuItem, FormControl, SelectChangeEvent, Chip, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { TABLE_TRANSACTION_TYPE_TRANSLATIONS } from './TransactionsTable';

interface TransactionActionsProps {
  type: string;
  startDate: DateTime;
  endDate: DateTime;
  loading: boolean;
  onTypeChange: (value: string) => void;
  onStartDateChange: (value: DateTime) => void;
  onEndDateChange: (value: DateTime) => void;
}

export const TransactionActions: React.FC<TransactionActionsProps> = ({
  type,
  startDate,
  endDate,
  loading,
  onTypeChange,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [selectedChip, setSelectedChip] = React.useState<string | null>(null);

  const handleTypeChange = (event: SelectChangeEvent) => {
    onTypeChange(event.target.value);
  };

  const handleChipSelect = (range: '7d' | '30d' | 'month') => {
    if (selectedChip === range) return;
    setSelectedChip(range);

    if (range === '7d') {
      onStartDateChange(DateTime.now().minus({ days: 7 }));
      onEndDateChange(DateTime.now());
    }

    if (range === '30d') {
      onStartDateChange(DateTime.now().minus({ days: 30 }));
      onEndDateChange(DateTime.now());
    }

    if (range === 'month') {
      onStartDateChange(DateTime.now().startOf('month'));
      onEndDateChange(DateTime.now());
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Chip
          color="primary"
          label="Últimos 7 dias"
          variant={selectedChip === '7d' ? 'filled' : 'outlined'}
          disabled={loading}
          onClick={() => handleChipSelect('7d')}
        />

        <Chip
          color="primary"
          label="Últimos 30 dias"
          variant={selectedChip === '30d' ? 'filled' : 'outlined'}
          disabled={loading}
          onClick={() => handleChipSelect('30d')}
        />

        <Chip
          color="primary"
          label="Mês atual"
          variant={selectedChip === 'month' ? 'filled' : 'outlined'}
          disabled={loading}
          onClick={() => handleChipSelect('month')}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }} mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Data inicial
          </Typography>

          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker
              value={startDate}
              onChange={(newValue) => {
                if (newValue) {
                  onStartDateChange(newValue);
                  setSelectedChip(null);
                }
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 37,
                      borderRadius: '8px',
                    },
                    '& input': {
                      fontSize: '14px',
                    },
                  }}
                  {...params}
                />
              )}
              maxDate={DateTime.now().minus({ days: 1 })}
            />
          </LocalizationProvider>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Data final
          </Typography>

          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker
              value={endDate}
              onChange={(newValue) => {
                if (newValue) {
                  onEndDateChange(newValue);
                  setSelectedChip(null);
                }
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 37,
                      borderRadius: '8px',
                    },
                    '& input': {
                      fontSize: '14px',
                    },
                  }}
                  {...params}
                />
              )}
              maxDate={DateTime.now()}
            />
          </LocalizationProvider>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <Typography variant="body2" fontWeight="500" color="#434241" mb={1}>
            Transação
          </Typography>

          <Select
            value={type}
            onChange={handleTypeChange}
            size="small"
            sx={{ borderRadius: '8px', height: 37, fontSize: '14px' }}
            disabled={loading}
          >
            <MenuItem value="Todas">Todas</MenuItem>
            {Object.entries(TABLE_TRANSACTION_TYPE_TRANSLATIONS).map((entry) => (
              <MenuItem key={entry[0]} value={entry[0]}>
                {entry[1]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </>
  );
};
